import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { MinifiedDispensary } from '@gcv/shared';
import { DueDiligenceStatus } from '@gcv/shared';
import { useQuery } from '@tanstack/react-query';

import { BanksApi } from 'api';
import { MINIFIED_DISPENSARIES_KEY } from 'hooks/keys';
import { useUser } from 'hooks/queries';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';

export function useMinifiedDispensaries(
  selectFn?: (data: MinifiedDispensary[]) => MinifiedDispensary[] | []
) {
  const navigate = useNavigate();
  const banksApi = new BanksApi();
  const bankDispensaryStore = getFiDispensaryStore();
  const { userBankId } = useUser();

  const {
    data: minifiedDispensaries,
    isLoading: isLoadingMinifiedDispensaries,
    isError: isMinifiedDispensariesError
  } = useQuery({
    enabled: !!userBankId.length,
    queryKey: [MINIFIED_DISPENSARIES_KEY, userBankId],
    queryFn: () => banksApi.getMinifiedDispensaries(userBankId),
    meta: {
      snackbarErrorMessage: 'Something went wrong! Please contact support if the problem persists.'
    },
    select: selectFn
  });

  const { dispensaries, archivedDispensaries } = useMemo(() => {
    if (!minifiedDispensaries) return { dispensaries: undefined, archivedDispensaries: undefined };

    const dispensaries = minifiedDispensaries.filter(
      ({ assigned_onboarding_template }) =>
        assigned_onboarding_template.status !== DueDiligenceStatus.BANK_DISCONNECTED
    );

    const archivedDispensaries = minifiedDispensaries.filter(
      ({ assigned_onboarding_template }) =>
        assigned_onboarding_template.status === DueDiligenceStatus.BANK_DISCONNECTED
    );

    return { dispensaries, archivedDispensaries };
  }, [minifiedDispensaries]);

  const dispensariesById = useMemo(() => {
    if (!minifiedDispensaries) return {};

    const map: Record<string, MinifiedDispensary> = {};
    minifiedDispensaries.forEach((dispensary) => {
      map[dispensary.id] = dispensary;
    });
    return map;
  }, [minifiedDispensaries]);

  useEffect(() => {
    if (dispensaries?.length) {
      bankDispensaryStore.setDispensaries(dispensaries);
    }
    if (archivedDispensaries?.length) {
      bankDispensaryStore.setArchivedDispensaries(archivedDispensaries);
    }
  }, [dispensaries, archivedDispensaries]);

  useEffect(() => {
    if (isMinifiedDispensariesError) {
      navigate('/error');
    }
  }, [isMinifiedDispensariesError]);

  return {
    minifiedDispensaries,
    dispensaries,
    archivedDispensaries,
    dispensariesById,
    isLoadingMinifiedDispensaries,
    isMinifiedDispensariesError
  };
}
